'use client';

import { createContext, useCallback, useContext, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ArrowRight, ArrowUpRight, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AuthModal from '@/components/public/auth-modal';
import { Squiggle } from '@/components/public/decor';
import { useAuth } from '@/hooks/useAuth';

/**
 * Lets any CTA inside the shell open the sign-up modal without the page
 * having to own modal state or thread callbacks down through its sections.
 */
const SignUpContext = createContext<() => void>(() => {});

export const useSignUp = () => useContext(SignUpContext);

const NAV = [
  { name: 'Features', href: '/features' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Contact', href: '/contact' },
];

const LEGAL = [
  { name: 'Terms', href: '/terms-of-service' },
  { name: 'Privacy', href: '/privacy-policy' },
  { name: 'Cookies', href: '/cookie-policy' },
  { name: 'Acceptable Use', href: '/acceptable-use-policy' },
];

function Header({ onSignUp }: { onSignUp: () => void }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  const active = (href: string) => pathname.startsWith(href);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b-2 border-pop-ink bg-background/95 backdrop-blur-lg">
      <div className="container flex h-16 items-center justify-between px-4">
        <Link href="/" className="flex items-center" aria-label="Logistr home">
          <Image
            src="/logo-full.png"
            alt="Logistr"
            width={160}
            height={40}
            className="h-6 w-auto"
            style={{ width: 'auto', height: '24px' }}
            priority
          />
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {NAV.map((item) => (
            <Link
              key={item.name}
              href={item.href}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                active(item.href)
                  ? 'bg-accent text-white'
                  : 'text-foreground/70 hover:bg-pop-amber hover:text-pop-ink'
              }`}
            >
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          {!isLoading &&
            (isAuthenticated ? (
              <Button variant="candy" size="default" asChild>
                <Link href="/dashboard">
                  Dashboard
                  <ArrowUpRight className="h-4 w-4" strokeWidth={2.5} />
                </Link>
              </Button>
            ) : (
              <>
                <Link
                  href="/login"
                  className="rounded-full px-4 py-2 text-sm font-bold text-foreground/70 transition-colors hover:text-foreground"
                >
                  Sign in
                </Link>
                <Button variant="candy" size="default" onClick={onSignUp}>
                  Start free
                  <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </Button>
              </>
            ))}
        </div>

        <button
          className="rounded-full border-2 border-pop-ink p-2 md:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" strokeWidth={2.5} /> : <Menu className="h-5 w-5" strokeWidth={2.5} />}
        </button>
      </div>

      {open && (
        <div className="absolute inset-x-0 top-16 border-b-2 border-pop-ink bg-background p-4 md:hidden">
          <nav className="space-y-2">
            {NAV.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                onClick={() => setOpen(false)}
                className={`block rounded-xl px-4 py-3 text-base font-bold ${
                  active(item.href) ? 'bg-accent text-white' : 'hover:bg-muted'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="mt-4 space-y-3 border-t-2 border-dashed border-border pt-4">
            {!isLoading &&
              (isAuthenticated ? (
                <Button variant="candy" size="pill" className="w-full" asChild>
                  <Link href="/dashboard" onClick={() => setOpen(false)}>Dashboard</Link>
                </Button>
              ) : (
                <>
                  <Button variant="candy-outline" size="pill" className="w-full" asChild>
                    <Link href="/login" onClick={() => setOpen(false)}>Sign in</Link>
                  </Button>
                  <Button
                    variant="candy"
                    size="pill"
                    className="w-full"
                    onClick={() => { setOpen(false); onSignUp(); }}
                  >
                    Start free
                  </Button>
                </>
              ))}
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="relative border-t-2 border-pop-ink bg-card">
      <Squiggle tone="amber" className="absolute -top-2 left-8 opacity-70" width={160} />

      <div className="container px-4 py-12">
        <div className="flex flex-col items-center justify-between gap-8 md:flex-row md:items-start">
          <div className="text-center md:text-left">
            <Image
              src="/logo-full.png"
              alt="Logistr"
              width={110}
              height={28}
              className="mx-auto h-6 w-auto md:mx-0"
            />
            <p className="mt-3 max-w-xs text-sm text-muted-foreground">
              Group your crew around every delivery, in the app or on WhatsApp.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-sm sm:grid-cols-3">
            <div className="space-y-2">
              <p className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground">Product</p>
              {NAV.map((item) => (
                <Link key={item.name} href={item.href} className="block font-medium hover:text-accent">
                  {item.name}
                </Link>
              ))}
            </div>
            <div className="space-y-2">
              <p className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground">Account</p>
              <Link href="/login" className="block font-medium hover:text-accent">Sign in</Link>
              <Link href="/signup" className="block font-medium hover:text-accent">Create account</Link>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground">Legal</p>
              {LEGAL.map((item) => (
                <Link key={item.name} href={item.href} className="block font-medium hover:text-accent">
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-10 border-t-2 border-dashed border-border pt-6 text-center text-xs text-muted-foreground">
          &copy; 2026 Logistr LLC. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

/**
 * Shell for every public marketing page.
 *
 * Owns the three things each page previously re-declared: the playful theme
 * scope, the header and the footer. Pages now render only their own content.
 */
export default function MarketingShell({ children }: { children: React.ReactNode }) {
  const [authOpen, setAuthOpen] = useState(false);
  const openSignUp = useCallback(() => setAuthOpen(true), []);

  return (
    <SignUpContext.Provider value={openSignUp}>
      <div className="theme-playful min-h-screen bg-background font-sans text-foreground">
        <Header onSignUp={openSignUp} />
        <main className="pt-16">{children}</main>
        <Footer />
        <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      </div>
    </SignUpContext.Provider>
  );
}
